/**
 * Reportes Contables API Service
 * Servicio para interactuar con la API de Reportes Financieros
 */

import api from './api';

// URL base para Contabilidad
const API_BASE_URL = (() => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  // Remover '/content' y agregar '/contabilidad'
  return baseURL.replace(/\/content\/?$/i, '/contabilidad');
})();

const reportesContablesService = {
  /**
   * Obtener Balance General
   * @param {string} fecha - Fecha del balance (formato YYYY-MM-DD). Opcional, por defecto hoy
   * @param {number} cuentaId - ID de la cuenta. Opcional, usa la cuenta activa del usuario
   * @returns {Promise} - Datos del balance general
   */
  getBalanceGeneral: (fecha = null, cuentaId = null) => {
    const params = {};
    if (fecha) params.fecha = fecha;
    if (cuentaId) params.cuenta_id = cuentaId;

    return api.get(`${API_BASE_URL}/reportes/balance-general/`, { params });
  },

  /**
   * Obtener Estado de Resultado
   * @param {string} fechaDesde - Fecha inicial (formato YYYY-MM-DD). Opcional
   * @param {string} fechaHasta - Fecha final (formato YYYY-MM-DD). Opcional, por defecto hoy
   * @param {number} cuentaId - ID de la cuenta. Opcional, usa la cuenta activa del usuario
   * @returns {Promise} - Datos del estado de resultado
   */
  getEstadoResultado: (fechaDesde = null, fechaHasta = null, cuentaId = null) => {
    const params = {};
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (cuentaId) params.cuenta_id = cuentaId;

    return api.get(`${API_BASE_URL}/reportes/estado-resultado/`, { params });
  },

  /**
   * Obtener Análisis de Cuentas
   * @param {string} fechaDesde - Fecha inicial (formato YYYY-MM-DD). Opcional
   * @param {string} fechaHasta - Fecha final (formato YYYY-MM-DD). Opcional, por defecto hoy
   * @param {string} tipoCuenta - Tipo de cuenta (ACTIVO, PASIVO, etc). Opcional
   * @param {boolean} soloConMovimientos - Solo mostrar cuentas con movimientos. Por defecto true
   * @param {number} cuentaId - ID de la cuenta. Opcional, usa la cuenta activa del usuario
   * @returns {Promise} - Análisis detallado de todas las cuentas
   */
  getAnalisisCuentas: (fechaDesde = null, fechaHasta = null, tipoCuenta = null, soloConMovimientos = true, cuentaId = null) => {
    const params = {};
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (tipoCuenta) params.tipo_cuenta = tipoCuenta;
    if (cuentaId) params.cuenta_id = cuentaId;
    params.solo_con_movimientos = soloConMovimientos;

    return api.get(`${API_BASE_URL}/reportes/analisis-cuentas/`, { params });
  },

  /**
   * Exportar Balance General a PDF (requiere html2canvas y jsPDF)
   * @param {object} balanceData - Datos del balance
   * @param {string} fecha - Fecha del balance
   * @param {string} nombreCuenta - Nombre de la cuenta
   */
  exportBalanceToPDF: async (balanceData, fecha, nombreCuenta) => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      // Crear elemento temporal con el contenido del balance
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="padding: 40px; font-family: Arial, sans-serif;">
          <h1 style="text-align: center; color: #333;">BALANCE GENERAL</h1>
          <h2 style="text-align: center; color: #666;">${nombreCuenta}</h2>
          <p style="text-align: center; color: #666;">Al ${fecha}</p>
          <!-- El contenido se construiría aquí -->
        </div>
      `;

      document.body.appendChild(element);

      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`balance-general-${fecha}.pdf`);

      document.body.removeChild(element);

      return true;
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      throw error;
    }
  },

  /**
   * Exportar Estado de Resultado a Excel (requiere xlsx)
   * @param {object} estadoData - Datos del estado de resultado
   * @param {string} fechaDesde - Fecha inicial
   * @param {string} fechaHasta - Fecha final
   */
  exportEstadoToExcel: async (estadoData, fechaDesde, fechaHasta) => {
    try {
      const XLSX = await import('xlsx');

      // Preparar datos para Excel
      const ingresos = estadoData.ingresos.map(i => ({
        Código: i.codigo,
        Cuenta: i.nombre,
        Monto: i.saldo
      }));

      const gastos = estadoData.gastos.map(g => ({
        Código: g.codigo,
        Cuenta: g.nombre,
        Monto: g.saldo
      }));

      const resumen = [
        { Concepto: 'Total Ingresos', Monto: estadoData.total_ingresos },
        { Concepto: 'Total Gastos', Monto: estadoData.total_gastos },
        { Concepto: 'Utilidad Neta', Monto: estadoData.utilidad_neta },
        { Concepto: 'Margen (%)', Monto: estadoData.margen }
      ];

      // Crear workbook
      const wb = XLSX.utils.book_new();

      // Agregar hojas
      const wsIngresos = XLSX.utils.json_to_sheet(ingresos);
      const wsGastos = XLSX.utils.json_to_sheet(gastos);
      const wsResumen = XLSX.utils.json_to_sheet(resumen);

      XLSX.utils.book_append_sheet(wb, wsIngresos, 'Ingresos');
      XLSX.utils.book_append_sheet(wb, wsGastos, 'Gastos');
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

      // Descargar
      XLSX.writeFile(wb, `estado-resultado-${fechaDesde}-${fechaHasta}.xlsx`);

      return true;
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      throw error;
    }
  },

  /**
   * Obtener detalles de un Asiento Contable
   * @param {number} asientoId - ID del asiento contable
   * @returns {Promise} - Datos del asiento contable
   */
  getAsientoContable: (asientoId) => {
    return api.get(`${API_BASE_URL}/asientos-contables/${asientoId}/`);
  }
};

export default reportesContablesService;
